import { Box, Stack, Typography } from '@mui/material';
import type { ColDef, SilobagRow } from '@siloreal:modules:private/silobag-list/config/extra/types';
import { FALLBACK_STRING, PLATFORM_GREEN } from './column-field-ui';
import * as HOOKS from '@siloreal:shared:hooks';

export function buildInputWeightColumn(): ColDef {
  return {
    field: 'input_weight',
    headerName: HOOKS.useLocale.getState().t('temp.common.weigth'),
    minWidth: 132,
    renderCell: ({ row }) => <InputWeightCell row={row} />,
  };
}

function InputWeightCell({ row }: { row: SilobagRow }) {
  const hasWeight = resolveHasWeight(row.input_weight);
  const weightLabel = formatInputWeight(row.input_weight);
  const i18n = HOOKS.useLocale();

  return (
    <Stack
      height="100%"
      justifyContent="center"
      onClick={(event) => event.stopPropagation()}
      sx={{ minWidth: 0, px: 1, py: 0.5 }}
    >
      <Box
        title={
          hasWeight
            ? `${i18n.t('temp.common.bagging_weigth')}: ${weightLabel}`
            : i18n.t('temp.common.weigth_unregistered')
        }
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.45,
          pl: 1,
          borderLeft: hasWeight
            ? `3px solid ${colorToRgba(PLATFORM_GREEN, 0.46)}`
            : `3px dashed ${colorToRgba('#a8a4a0', 0.35)}`,
          opacity: hasWeight ? 1 : 0.42,
        }}
      >
        <Typography
          noWrap
          sx={{
            fontSize: 13,
            fontWeight: hasWeight ? 600 : 500,
            lineHeight: 1.25,
            letterSpacing: '0.01em',
            color: hasWeight ? '#3A3440' : '#a8a4a0',
            minWidth: 0,
          }}
        >
          {hasWeight ? weightLabel : FALLBACK_STRING}
        </Typography>

        {hasWeight ? (
          <Box
            component="span"
            sx={{
              alignSelf: 'flex-start',
              px: 0.65,
              py: 0.15,
              borderRadius: '4px',
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.02em',
              lineHeight: 1.35,
              bgcolor: colorToRgba(PLATFORM_GREEN, 0.14),
              color: PLATFORM_GREEN,
              border: `1px solid ${colorToRgba(PLATFORM_GREEN, 0.24)}`,
              boxShadow: `inset 0 1px 0 ${colorToRgba(PLATFORM_GREEN, 0.1)}`,
            }}
          >
            {i18n.t('temp.common.bagging')}
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '0.02em',
              lineHeight: 1.35,
              color: '#b8b4ad',
            }}
          >
            {i18n.t('temp.common.without_data')}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function resolveHasWeight(value: string | null | undefined): boolean {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) && numeric > 0;
}

function formatInputWeight(value: string | null | undefined): string {
  const numeric = parseFloat(value ?? '0');

  if (!Number.isFinite(numeric)) return FALLBACK_STRING;

  return `${numeric.toLocaleString('es-AR')} kg`;
}

function colorToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;

  const red = parseInt(expanded.slice(0, 2), 16);
  const green = parseInt(expanded.slice(2, 4), 16);
  const blue = parseInt(expanded.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
